import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  IconWorldDownload,
  IconTransfer,
  IconAppWindow,
  IconCircleCheckFilled,
  IconLoader2,
  IconWorld,
  IconBolt,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppStore } from "../../../store/app";
import styles from "./styles.module.css";

const nodeVariant = {
  hidden: { scale: 0.8, opacity: 0, y: 10 },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "backOut" },
  },
};

const lineVariant = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.8, ease: "easeInOut" },
  },
};

export default function OnboardingPanel() {
  const { onboarding } = useAppStore((state) => state.settings);
  const { website } = onboarding;
  const {
    loading,
    fetchingWebsite,
    creatingAgent,
    teachingAgent,
    error,
    step,
  } = onboarding;
  const intl = useIntl();

  const steps = [
    {
      id: "fetch",
      icon: <IconWorldDownload size={20} />,
      text: intl.formatMessage({ id: "ai.onboarding.readingWebsite" }),
      done: Boolean(fetchingWebsite),
    },
    {
      id: "teach",
      icon: <IconTransfer size={20} />,
      text: intl.formatMessage({ id: "ai.onboarding.teachingAgent" }),
      done: Boolean(teachingAgent),
    },
    {
      id: "create",
      icon: <IconAppWindow size={20} />,
      text: intl.formatMessage({ id: "ai.onboarding.creatingAgent" }),
      done: Boolean(creatingAgent),
    },
  ];

  const currentStepIndex = steps.findIndex((s) => !s.done);
  const isViewActive =
    loading || fetchingWebsite || teachingAgent || creatingAgent;

  return step === 0 ? (
    <div className={styles.panelContainer}></div>
  ) : (
    <div className={styles.panelContainer}>
      <AnimatePresence mode="wait">
        {isViewActive ? (
          <motion.div
            key="loading-view"
            className={styles.loadingWrapper}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className={styles.loadingTitle}>
              <span className={styles.magicText}>
                <FormattedMessage id="ai.onboarding.building" />
              </span>
            </h3>

            <div className={styles.timelineContainer}>
              {steps.map((s, index) => {
                const isLast = index === steps.length - 1;
                const isActive = index === currentStepIndex && loading;

                return (
                  <div key={s.id} className={styles.timelineItem}>
                    <div className={styles.timelineLeft}>
                      {!isLast && (
                        <div
                          className={`${styles.connectorLine} ${
                            s.done ? styles.lineDone : ""
                          }`}
                        />
                      )}

                      <div
                        className={`${styles.iconCircle} ${
                          s.done
                            ? styles.circleDone
                            : isActive
                            ? styles.circleActive
                            : ""
                        }`}
                      >
                        {s.done ? (
                          <IconCircleCheckFilled size={20} />
                        ) : isActive ? (
                          <IconLoader2 size={20} className={styles.spinIcon} />
                        ) : (
                          <div className={styles.waitingDot} />
                        )}
                      </div>
                    </div>

                    <motion.div
                      className={`${styles.stepCard} ${
                        isActive ? styles.cardActive : ""
                      } ${s.done ? styles.cardDone : ""}`}
                      layout
                    >
                      <div className={styles.stepContent}>
                        <span className={styles.stepIconWrapper}>{s.icon}</span>
                        <span className={styles.stepText}>{s.text}</span>
                      </div>

                      {isActive && (
                        <div className={styles.processingBadge}>
                          <span className={styles.dotPulse}></span>
                          <FormattedMessage id="ai.onboarding.agent.processing" />
                        </div>
                      )}
                    </motion.div>
                  </div>
                );
              })}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-danger mt-3 fs-6 text-center"
              >
                {intl.formatMessage({ id: "ai.onboarding.error" })}
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="viz-view"
            className={styles.vizWrapper}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className={styles.glowBackground} />

            <div className={styles.diagramScene}>
              <motion.div
                className={`${styles.dataNode} ${
                  website ? styles.nodeActive : ""
                }`}
                initial="hidden"
                animate={website ? "visible" : "hidden"}
                variants={nodeVariant}
                style={{ top: "15%", right: "5%" }}
              >
                <div className={styles.nodeIcon}>
                  <IconWorld size={20} />
                </div>
                <div className={styles.nodeLabel}>
                  <span>
                    <FormattedMessage
                      id="ai.biz.knowledge"
                      defaultMessage="Conocimiento"
                    />
                  </span>
                  <strong>{website || "..."}</strong>
                </div>
              </motion.div>
              <svg className={styles.connectionSvg}>
                {website && (
                  <motion.path
                    d="M380,80 Q250,200 250,250"
                    fill="none"
                    stroke="var(--visito-color-1)"
                    strokeWidth="2"
                    strokeDasharray="6 4"
                    variants={lineVariant}
                    initial="hidden"
                    animate="visible"
                  />
                )}
              </svg>

              <div className={styles.coreWrapper}>
                <div className={styles.coreCircle}>
                  <IconBolt size={48} stroke={1.5} />
                </div>
                <div
                  className={styles.pulseRing}
                  style={{ animationDelay: "0s" }}
                />
                <div
                  className={styles.pulseRing}
                  style={{ animationDelay: "1.5s" }}
                />
              </div>

              <div className={styles.vizFooter}>
                <h3>
                  <FormattedMessage id="ai.viz.title" />
                </h3>
                <p>
                  <FormattedMessage id="ai.viz.description" />
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
